import React, { useCallback, useRef } from "react";
import _ from "lodash";
import copy from "copy-to-clipboard";
import EditIconButton from "components/EditIconButton";
import message from "components/CustomMessage";
import Vocabulary from "components/Vocabulary";
import { CellRenderers } from "utils/tableUtils";
import { PermissionSetMap } from "constants/RBACConstants";
import { useFindingDetails } from "./FindingDetailsProvider";
import AuthorisedContainer from "components/AuthorisedContainer";
import { ExternalLinkIcon, LoadingIcon } from "components/Icons";
import { CollapseHeaderDivider } from "../FindingDetailsModalV2";
import { Collapse, Spin, Typography } from "antd";
import {
  isCustomFinding,
} from "../utils";
import { SPECIFIC_FIELD, TOOL_TYPE_SPECIFIC_FIELDS  } from '../Constants'
import { FindingDetailsGrid } from "./FindingDetailsGrid";
import CopyIcon from "ac-components/ac-icons/CopyIcon";
import ACButton from "ac-components/ac-button/ACButton";
import { ToolsData } from "views/Integrations/new/util";
import NameAvatar from "components/NameAvatar";
import ACSeverityTag from "ac-components/ACSeverityTag";

const { Link } = Typography;
const { Panel } = Collapse;

export function FindingDetailsSourceInfo({ designV3 = true }) {
  const { loading, finding, openFindingFieldEditPopup } = useFindingDetails();

  return (
    <Collapse
      className={`${
        !designV3
          ? "collapse-v2 finding-detail-source-info"
          : "finding-detailV2-source-info"
      }`}
    >
      <Panel header={getHeader(finding, loading, designV3, openFindingFieldEditPopup)} key="sourceInfo">
        <Spin spinning={loading}>
          <FindingDetailsGrid list={getList(loading, finding, openFindingFieldEditPopup)} data={finding} />
        </Spin>
      </Panel>
    </Collapse>
  );
}

export const getList = (loading, finding, openFindingFieldEditPopup, featureFlags) => {
  const { sourceCodeDetails, source, additionalDetails } = finding || {};
  const _isCustomFinding = isCustomFinding(finding?.testType);

  const sourceInfoField = [
    {
      name: "TOOL_SEVERITY",
      label: "Tool Severity",
      path: "toolSeverity",
      formatter: (toolSeverity) => <ACSeverityTag value={toolSeverity} isCustomizedValue={false} />
    },

    {
      name: "TOOL_FINDING_CATEGORY",
      label: (
        <>
          Tool <Vocabulary name="category" />
        </>
      ),
      path: "findingCategory",
      editAction: _isCustomFinding
        ? () => openFindingFieldEditPopup("findingCategory")
        : undefined,
      actionPermission: PermissionSetMap.UPDATE_FINDINGS,
    },

    {
      name: "TOOL_FINDING_ID",
      label: 'Tool Finding ID',
      path: "toolId",
      formatter: (toolId, finding) => {
        return finding?.source?.ref ? (
          <Link href={cleanUrl(finding.source.ref)} target="_blank" title={toolId}>
            {toolId || (
              <>
                <ExternalLinkIcon /> view in source
              </>
            )}
          </Link>
        ) : (
          CellRenderers.VALUE_OR_NA(toolId)
        );
      },
      editAction: _isCustomFinding
        ? () => openFindingFieldEditPopup("toolId")
        : undefined,
      extra: finding?.toolId ? (
        <CopyContent content={finding.toolId} fieldLabel="Tool Finding ID" />
      ) : null,
      actionPermission: PermissionSetMap.UPDATE_FINDINGS,
    },
      {
        name: "TOOL_FINDING_STATUS",
        label: "Tool Finding Status",
        path: "additionalDetails.toolStatusOriginal"
      },
    {
      name: "SCAN_ID",
      label: "Scan ID",
      path: "source.id",
      formatter: (value) => `S-${value}`
    },
    {
      name: "TOOL_FINDING_SUB_STATUS_ORIGINAL",
      label: <>{loading && !finding ? <LoadingIcon/> : source.name} Tool Sub Status</>,
      path: "additionalDetails.toolSubStatusOriginal"
    },
    !_.isNil(additionalDetails?.repositoryName) &&
      {
        name: "CODE_REPO_LINK",
        label: "Repository",
        path: "additionalDetails.repositoryName",
        className: "text-rtl",
        valueClassName: "break-content",
        formatter: (__, finding) => {
          return (
            additionalDetails.repositoryUrl ? 
            <Typography.Link
              className="repo-link"
              href={_.get(finding, "additionalDetails.repositoryUrl")}
              target="_blank"
            >
              {
                <>
                  {shortenPath(_.get(finding, "additionalDetails.repositoryName"))}
                  <ExternalLinkIcon
                    className={"m-l-sm repolink-external-icon"}
                  />
                </>
              }
            </Typography.Link>
            : CellRenderers.VALUE_OR_NA(additionalDetails.repositoryName)
          );
        },
      },
    {
      name: "FILE_NAME",
      label: "File Name",
      path: "sourceCodeDetails.filePath",
      className: "text-rtl",
      containerClassName: 'flex-column',
      formatter: (__, finding) => {
        return (
          sourceCodeDetails.link ? 
          <Typography.Link
            className="repo-link"
            href={_.get(finding, "sourceCodeDetails.link")}
            target="_blank"
          >
            {
              <>
                {shortenPath(_.get(finding, "sourceCodeDetails.filePath"))}
                <ExternalLinkIcon
                  className={"m-l-sm repolink-external-icon"}
                />
              </>
            }
          </Typography.Link> : CellRenderers.VALUE_OR_NA(sourceCodeDetails.filePath)
        );
      },
      extra: finding?.fileName ? (
        <CopyContent content={finding.fileName} fieldLabel="File Name"/>
      ) : null
    },

    {
      name: "LINE_NUMBER",
      label: "Line Number",
      path: "sourceCodeDetails.lineNumber",
      className: "text-rtl",
      formatter: CellRenderers.VALUE_OR_NA,
    },


    {
      name: "IMAGE_NAME",
      label: "Image Name",
      path: "imageName",
      formatter: (imageName, finding) => {
        return CellRenderers.VALUE_OR_NA(
          _.compact([
            imageName,
            _.get(finding, ["additionalDetails", "containerImageTag"]),
          ]).join(":")
        );
      },
    },

    {
      name: "FIXABLE_USING_TOOL",
      label: "Fixable Using Tool",
      path: "fixability",
    },

    {
      name: "EXPLOIT_MATUARITY",
      label: "Exploit Maturity",
      path: "additionalDetails.SNYK_exploitMaturity",
    },

    {
      name: "DEVICE_HOST_NAME",
      label: "Device",
      path: "additionalDetails.vmHostName",
      formatter: CellRenderers.VALUE_OR_NA,
    },

    {
      name: "IP_ADDRESSES",
      label: "IP Addresses",
      path: "vmHostIpAddresses",
      formatter: (vmHostIpAddresses) => {
        if (!_.isEmpty(vmHostIpAddresses)) {
          return vmHostIpAddresses.join(", ");
        }
        return CellRenderers.VALUE_OR_NA();
      },
    },

    {
      name: "ENDPOINT",
      label: "Endpoint",
      path: "newUrl",
      containerClassName: 'flex-column',
      editAction: _isCustomFinding
        ? () => openFindingFieldEditPopup("newUrl")
        : undefined,
      actionPermission: PermissionSetMap.UPDATE_FINDINGS,
      formatter: CellRenderers.VALUE_OR_NA,
      extra: finding?.newUrl ? (
        <CopyContent content={finding.newUrl} fieldLabel="Endpoint"/>
      ) : null
    },

    {
      name: "AUTHOR_DEVELOPER",
      label: "Developer",
      path: "developer",
    },
    {
      name: "COMPONENT_NAME",
      label: "Component Name",
      path: "componentName",
      containerClassName: 'flex-column',
      formatter: (componentName, finding) =>
        featureFlags.SBOM_FINDING_REDIRECTION && componentName
          ? <Link href={`#/sbom?filters=${encodeURIComponent(JSON.stringify({ name: [`"${componentName}"`] }))}`} target="_blank">{componentName}</Link>
          : CellRenderers.VALUE_OR_NA(componentName),
      editAction: _isCustomFinding
        ? () => openFindingFieldEditPopup("componentName")
        : undefined,
      actionPermission: PermissionSetMap.UPDATE_FINDINGS,
    },
    {
      name: "COMPONENT_VERSION",
      label: "Component Affected Version",
      path: "componentVersion",
      formatter: (componentVersion, finding) =>
        featureFlags.SBOM_FINDING_REDIRECTION && componentVersion
          ? <Link href={`#/sbom?filters=${encodeURIComponent(JSON.stringify({ name: [`"${finding?.componentName}"`], version: [componentVersion] }))}`} target="_blank">{componentVersion}</Link>
          : CellRenderers.VALUE_OR_NA(componentVersion),
      editAction: _isCustomFinding
        ? () => openFindingFieldEditPopup("componentVersion")
        : undefined,
      actionPermission: PermissionSetMap.UPDATE_FINDINGS,
    },
    {
      name: "COMPONENT_FIX_VERSION",
      label: "Component Fix Version",
      path: "additionalDetails.componentFixVersions",
      formatter: CellRenderers.VALUE_OR_NA,
      editAction: _isCustomFinding
        ? () => openFindingFieldEditPopup("componentFixVersions")
        : undefined,
      actionPermission: PermissionSetMap.UPDATE_FINDINGS,
    },
  ];
  const fieldArray = TOOL_TYPE_SPECIFIC_FIELDS[finding?.scanType];
  if (fieldArray) {
    return sourceInfoField.filter((tool) => fieldArray.includes(tool.name));
  } else {
    return sourceInfoField.filter((tool) =>
      SPECIFIC_FIELD.includes(tool.name)
    );
  }
};

export const getHeader = (finding, loading, designV3, openFindingFieldEditPopup) => {
  const _isCustomFinding = isCustomFinding(finding?.testType);

  const toolImage = ToolsData.find((tool) => tool.name === finding?.source.name)?.icon;

  const onEditClick = (e) => {
      e.stopPropagation();
      openFindingFieldEditPopup("scanType");
    };

  return loading && !finding ? (
    <>
      SOURCE INFO <LoadingIcon />
    </>
  ) : (
    <>
      {designV3 ? (
        <>
          {
            toolImage ?
              <NameAvatar className='m-r-xs' style={{ height: 24, width: 25 }} icon={toolImage} name={''} />
              :
              null
          }
          {finding.source.name} <span className="source-tool-type">{CellRenderers.VALUE_OR_NA(finding.scanType)}</span>
        </>
      ) : (
        <>
          {finding.source.name.toUpperCase()}
          <CollapseHeaderDivider />
          {CellRenderers.VALUE_OR_NA(finding.scanType)}
        </>
      )}
      {_isCustomFinding ? (
        <AuthorisedContainer permission={PermissionSetMap.UPDATE_FINDINGS}>
          <EditIconButton className={"m-l-xs"} onClick={onEditClick} />
        </AuthorisedContainer>
      ) : null}
    </>
  )}

export function CopyContent({ content, fieldLabel="Link", style = {} }) {
  const messageRef = useRef();

  const copyTextToClipBoard = useCallback(() => {
    if (messageRef.current) {
      messageRef.current();
    }
    copy(content);
    messageRef.current = message.success(fieldLabel + " copied to clipboard");
  }, [content, fieldLabel]);

  return (
    <ACButton
      size="sm"
      type="ghost"
      iconOnly
      onClick={copyTextToClipBoard}
      style={style}
    >
      <CopyIcon />
  </ACButton>
  );
}

function shortenPath(path) {
  const parts = path.split('/');
  
  if (parts.length <= 2) {
      return path;
  }

  const firstPart = parts[0];
  const lastPart = parts[parts.length - 1];
  const middlePart = '...';

  return <span title={path}>{firstPart}/{middlePart}/{lastPart}</span>;
};

//cleanUrl is required to handle below URL format we recieve from Backend
//https://abcd?filters={\"alert.id\":[\"" + alertId + "\"],\"alert.status\":[\"dismissed\",\"open\",\"resolved\",\"snoozed\",\"pending_resolution\"]}

function cleanUrl(url) {
  if (!url.includes('?filters=')) return url;
  //Using encodeURIComponent and decodeURIComponent,
  //Decoding to make the filters part into a JSON format
  //and when we use encodeURIComponent, it encodes the URL easily
  const [baseUrl, filters] = url.split('?filters=');
  const encodedFilters = encodeURIComponent(decodeURIComponent(filters));
  return `${baseUrl}?filters=${encodedFilters}`;
}


